import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  BranchDto,
  ReportExportRequest,
  SalesTimingHourRow,
  SalesTimingReport,
  SalesTimingWeekdayRow,
} from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { KpiCard } from '@/components/KpiCard';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { ReportPeriodButton, ReportToolbar } from '@/components/ReportToolbar';
import { DataTable, TableScroll, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { ExportMenu } from '@/components/ExportMenu';
import { downloadReportExport } from '@/features/reports/api';
import { exportSubtitle } from '@/features/reports/export-table';
import type { ExportFormat } from '@/features/reports/api';
import { fetchSalesTiming, salesTimingKeys } from '@/features/reports/sales-timing-api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import type { ReportFilters } from '@/features/reports/useReportFilters';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { usePortal } from '@/features/portal/PortalContext';

type Metric = 'bills' | 'netSales';

const money = (value: number) => value.toFixed(2);
const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;
const hourRange = (hour: number) => `${hourLabel(hour)}–${hourLabel((hour + 1) % 24)}`;
const metricValue = (row: { bills: number; netSales: number }, metric: Metric) =>
  metric === 'bills' ? row.bills : row.netSales;

const WEEKDAY_COLUMNS: Column<SalesTimingWeekdayRow>[] = [
  { key: 'label', header: 'Day', pinned: true, cell: (row) => row.label },
  { key: 'bills', header: 'Bills', align: 'right', cell: (row) => String(row.bills) },
  { key: 'netSales', header: 'Net sales', align: 'right', cell: (row) => money(row.netSales) },
  { key: 'averageBill', header: 'Avg bill', align: 'right', cell: (row) => money(row.averageBill) },
];

const HOUR_COLUMNS: Column<SalesTimingHourRow>[] = [
  { key: 'hour', header: 'Hour', pinned: true, cell: (row) => hourRange(row.hour) },
  { key: 'bills', header: 'Bills', align: 'right', cell: (row) => String(row.bills) },
  { key: 'netSales', header: 'Net sales', align: 'right', cell: (row) => money(row.netSales) },
  { key: 'averageBill', header: 'Avg bill', align: 'right', cell: (row) => money(row.averageBill) },
];

/** The span of hours that saw any trade, so a 9-to-9 shop's chart isn't mostly empty night. */
function activeHours(report: SalesTimingReport): number[] {
  const busy = report.byHour.filter((h) => h.bills > 0).map((h) => h.hour);
  if (busy.length === 0) return [];
  const first = Math.min(...busy);
  const last = Math.max(...busy);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

function untimedNote(report: SalesTimingReport): string {
  const { bills, netSales } = report.untimed;
  return bills > 0
    ? `${bills} bill${bills === 1 ? '' : 's'} (${money(netSales)}) had no readable time: counted by weekday and in the totals, but not by hour.`
    : '';
}

const NET_SALES_NOTE =
  'Net sales are bill totals including VAT, less refunds against those bills by returns in the window — a return has no time of its own, so it comes off the hour the bill was rung up. Voided bills and opening balances are left out.';

/**
 * Both tables on the page in one sheet — the weekday rows, then the hours
 * that saw trade — told apart by a leading Breakdown column. The totals row
 * is the window's, once; it is not the sum of both halves.
 */
function buildExport(
  report: SalesTimingReport,
  filters: ReportFilters,
  branches: readonly BranchDto[],
): ReportExportRequest {
  const line = (breakdown: string, label: string, row: { bills: number; netSales: number; averageBill: number }) => [
    breakdown,
    label,
    String(row.bills),
    money(row.netSales),
    money(row.averageBill),
  ];
  const docType = filters.docType === 'pos' ? 'POS' : filters.docType === 'invoice' ? 'Invoice' : '';
  const untimed = untimedNote(report);
  return {
    title: 'Busy hours',
    subtitle: [exportSubtitle(report, { branchId: filters.branchId, branches }), docType].filter(Boolean).join(' · '),
    from: report.from,
    to: report.to,
    columns: [
      { header: 'Breakdown' },
      { header: 'Day / hour' },
      { header: 'Bills', align: 'right', numeric: true },
      { header: 'Net sales', align: 'right', money: true },
      { header: 'Avg bill', align: 'right', money: true },
    ],
    rows: [
      ...report.byWeekday.map((row) => line('Weekday', row.label, row)),
      ...activeHours(report).map((h) => line('Hour', hourRange(h), report.byHour[h])),
    ],
    totals: {
      label: 'Total',
      // The label fills the first column; cells cover the rest.
      cells: ['', String(report.totals.bills), money(report.totals.netSales), money(report.totals.averageBill)],
    },
    notes: [NET_SALES_NOTE, ...(untimed ? [untimed] : [])],
  };
}

function MetricToggle({ value, onChange }: { value: Metric; onChange: (m: Metric) => void }) {
  const options: { value: Metric; label: string }[] = [
    { value: 'netSales', label: 'Net sales' },
    { value: 'bills', label: 'Bills' },
  ];
  return (
    <div className="flex overflow-hidden rounded-lg border border-slate-300" role="group" aria-label="Chart measure">
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={`px-3 py-2 text-sm font-medium transition-colors ${index > 0 ? 'border-l border-slate-300' : ''} ${
            value === option.value ? 'bg-brand-600/[0.12] text-brand-700' : 'bg-white text-slate-700 hover:bg-slate-100'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** One plain CSS column per hour, scaled to the busiest. */
function HourBars({ report, hours, metric }: { report: SalesTimingReport; hours: number[]; metric: Metric }) {
  const rows = hours.map((h) => report.byHour[h]);
  const max = Math.max(0, ...rows.map((r) => metricValue(r, metric)));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex h-48 items-end gap-1" role="img" aria-label="Sales by hour of day">
        {rows.map((row) => {
          const v = metricValue(row, metric);
          const height = max > 0 ? Math.max(0, (v / max) * 100) : 0;
          return (
            <div
              key={row.hour}
              className="flex h-full min-w-0 flex-1 flex-col justify-end"
              title={`${hourRange(row.hour)}: ${row.bills} bills, ${money(row.netSales)}`}
            >
              <div className="rounded-t bg-emerald-600" style={{ height: `${height}%`, minHeight: v > 0 ? 2 : 0 }} />
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex gap-1 text-[10px] tabular-nums text-slate-500" aria-hidden="true">
        {rows.map((row) => (
          <div key={row.hour} className="min-w-0 flex-1 text-center">
            {String(row.hour).padStart(2, '0')}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Weekday rows × hour columns, each cell shaded by its share of the busiest cell. */
function HeatTable({ report, hours, metric }: { report: SalesTimingReport; hours: number[]; metric: Metric }) {
  const cell = new Map(report.heat.map((c) => [`${c.weekday}:${c.hour}`, c]));
  const max = Math.max(0, ...report.heat.map((c) => metricValue(c, metric)));
  return (
    <TableScroll label="Hour by weekday">
      <table className="w-full border-separate border-spacing-0.5 text-xs">
        <caption className="sr-only">
          {metric === 'bills' ? 'Bills' : 'Net sales'} by weekday and hour of day
        </caption>
        <thead>
          <tr>
            <th scope="col" className="px-2 py-1 text-left font-medium text-slate-500">
              Day
            </th>
            {hours.map((h) => (
              <th key={h} scope="col" className="px-1 py-1 text-center font-medium tabular-nums text-slate-500">
                {String(h).padStart(2, '0')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.byWeekday.map((day) => (
            <tr key={day.weekday}>
              <th scope="row" className="px-2 py-1 text-left font-medium text-slate-700">
                {day.label}
              </th>
              {hours.map((h) => {
                const c = cell.get(`${day.weekday}:${h}`);
                const v = c ? metricValue(c, metric) : 0;
                const ratio = max > 0 ? Math.max(0, v / max) : 0;
                return (
                  <td
                    key={h}
                    className={`h-8 min-w-[2.75rem] rounded px-1 text-center tabular-nums ${
                      ratio > 0.55 ? 'text-white' : 'text-slate-700'
                    }`}
                    style={{ backgroundColor: c ? `rgba(5, 150, 105, ${0.08 + ratio * 0.82})` : 'rgb(248, 250, 252)' }}
                    title={`${day.label} ${hourRange(h)}: ${c?.bills ?? 0} bills, ${money(c?.netSales ?? 0)}`}
                  >
                    {c ? (metric === 'bills' ? String(v) : Math.round(v).toLocaleString()) : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

export function SalesTimingPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const [metric, setMetric] = useState<Metric>('netSales');

  const query = useQuery({
    queryKey: salesTimingKeys.report(filters),
    queryFn: ({ signal }) => fetchSalesTiming(filters, signal),
  });

  return (
    <div>
      <PageHeader
        title="Busy hours"
        description="When the shop sells — bills and net sales by hour of day and by weekday."
      />
      <ReportFilterBar
        filters={filters}
        onChange={setFilters}
        branches={profile?.branches ?? []}
        showDocType
        hideDateRange
      />
      <ReportToolbar>
        <ReportPeriodButton from={filters.from} to={filters.to} onChange={toolbar.setRange} />
        {query.data && query.data.totals.bills > 0 ? (
          <div className="ml-auto">
            <ExportMenu
              onExport={(format: ExportFormat) =>
                downloadReportExport(buildExport(query.data, filters, profile?.branches ?? []), format)
              }
            />
          </div>
        ) : null}
      </ReportToolbar>
      <AsyncSection
        state={query}
        isEmpty={(report: SalesTimingReport) => report.totals.bills === 0}
        emptyTitle="No sales in this window"
        skeleton={<TableSkeleton rows={8} columns={4} />}
      >
        {(report: SalesTimingReport) => {
          const hours = activeHours(report);
          const busiestHour = [...report.byHour].sort((a, b) => b.netSales - a.netSales)[0];
          const busiestDay = [...report.byWeekday].sort((a, b) => b.netSales - a.netSales)[0];
          return (
            <div className="space-y-8">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <KpiCard label="Bills" value={String(report.totals.bills)} />
                <KpiCard label="Net sales" value={money(report.totals.netSales)} />
                <KpiCard label="Busiest hour" value={busiestHour.bills > 0 ? hourRange(busiestHour.hour) : '—'} />
                <KpiCard label="Busiest day" value={busiestDay.bills > 0 ? busiestDay.label : '—'} />
              </div>

              {hours.length > 0 ? (
                <section>
                  <div className="mb-3 flex items-center justify-between gap-4">
                    <h2 className="text-sm font-semibold text-slate-900">By hour of day</h2>
                    <MetricToggle value={metric} onChange={setMetric} />
                  </div>
                  <HourBars report={report} hours={hours} metric={metric} />
                </section>
              ) : null}

              {hours.length > 0 ? (
                <section>
                  <h2 className="mb-3 text-sm font-semibold text-slate-900">
                    Hour × weekday — {metric === 'bills' ? 'bills' : 'net sales'}
                  </h2>
                  <HeatTable report={report} hours={hours} metric={metric} />
                </section>
              ) : null}

              <section>
                <h2 className="mb-3 text-sm font-semibold text-slate-900">By weekday</h2>
                <DataTable
                  caption="Sales by weekday"
                  columns={WEEKDAY_COLUMNS}
                  rows={report.byWeekday}
                  rowKey={(row) => String(row.weekday)}
                  totals={{
                    label: 'Total',
                    cells: {
                      bills: String(report.totals.bills),
                      netSales: money(report.totals.netSales),
                      averageBill: money(report.totals.averageBill),
                    },
                  }}
                />
              </section>

              <section>
                <h2 className="mb-3 text-sm font-semibold text-slate-900">By hour</h2>
                <DataTable
                  caption="Sales by hour of day"
                  columns={HOUR_COLUMNS}
                  rows={hours.map((h) => report.byHour[h])}
                  rowKey={(row) => String(row.hour)}
                />
              </section>

              <p className="text-xs text-slate-500">
                {NET_SALES_NOTE}
                {report.untimed.bills > 0 ? ` ${untimedNote(report)}` : ''}
              </p>
            </div>
          );
        }}
      </AsyncSection>
    </div>
  );
}
